import { useState, useEffect, ReactNode, FC } from 'react';
import { useIntl } from 'react-intl';

// common
import { UserInfoDataContext, type UserInfoDataContextType } from 'modules/context';
import useGeneralHook from 'common/hook/useGeneralHook';
import useUserRoles from 'common/hook/useUserRoles';

// screen
import useGetUserInfo from 'modules/user-info/services/useGetUserInfo';
import { defaultUserDetail, type UserDetail } from 'modules/account/services/useGetAccountInfo';
import { convertOrgType } from 'modules/account/utils';
import useUpdatePasswordLoggedInUser, {
  defaultPasswordFields,
  type Password,
} from 'modules/user-info/services/useUpdatePasswordLoggedInUser';
import useUpdateInfoLoggedInUser from 'modules/user-info/services/useUpdateInfoLoggedInUser';

interface UserInfoDataProviderProps {
  children: ReactNode;
}

/**
 * Provider component for UserInfo data and actions.
 *
 * This component fetches the user information and provides it to its children via context.
 *
 * It also provides functions to update the user information and password.
 */
export const UserInfoDataProvider: FC<UserInfoDataProviderProps> = (props) => {
  const { children } = props;

  const [userInfo, setUserInfo] = useState<UserDetail>(defaultUserDetail);
  const [password, setPasswordState] = useState<Password>(defaultPasswordFields);

  const { formatMessage } = useIntl();
  const { isClient } = useUserRoles();
  const { enqueueSnackbar } = useGeneralHook();

  const { data: accountData } = useGetUserInfo();
  const updateUserInfo = useUpdateInfoLoggedInUser();
  const changePassword = useUpdatePasswordLoggedInUser();

  const setUserData = (fieldName: string, value: any) => {
    if (!fieldName.includes('.')) {
      setUserInfo((old) => ({ ...old, [fieldName]: value }));
      return;
    }

    const parts = fieldName.split('.');
    setUserInfo((old) => ({
      ...old,
      [parts[0]]: {
        ...(old[parts[0]] as object),
        [parts[1]]: value,
      },
    }));
  };

  const setPassword = (password: Password) => {
    setPasswordState(password);
  };

  const setDefaultPassword = () => {
    setPasswordState(defaultPasswordFields);
  };

  const handleUpdatePassword = () => {
    const { currentPassword, newPassword, newPasswordRetype } = password;
    if (!currentPassword) return;

    const newPasswordTyped = Boolean(newPassword || newPasswordRetype);
    const invalidNewPassword = Boolean(currentPassword && !newPasswordTyped);

    if (invalidNewPassword) {
      enqueueSnackbar({
        message: formatMessage({
          id: 'userInfo.error.fillAllNeededDataOnChangePasswordTab',
        }),
        variant: 'error',
      });
      return;
    }

    if (newPassword !== newPasswordRetype) {
      enqueueSnackbar({
        message: formatMessage({ id: 'userInfo.error.invalidNewPassword' }),
        variant: 'error',
      });
      return;
    }

    changePassword({
      onSetDefaultPassword: setDefaultPassword,
      oldPassword: currentPassword,
      newPassword: newPassword,
    });
  };

  const handleUpdateUserInfo = () => {
    updateUserInfo({
      newUserInfo: {
        title: userInfo.title,
        name: userInfo.name,
        email: `${userInfo.email}`,
        miscData: {
          JobPosition: userInfo.miscData.JobPosition || '',
          Phone: userInfo.miscData.Phone || '',
          Address: userInfo.miscData.Address || '',
          IdDoc: userInfo.miscData.IdDoc || '',
          IdDocIssuedAt: `${userInfo.miscData.IdDocIssuedAt || ''}`,
          IdDocIssuedBy: (userInfo.miscData.IdDocIssuedBy as string) || '',
          Dob: `${userInfo.miscData.Dob || ''}`,
          Gender: userInfo.miscData.Gender || '',
        },
      },
    });

    handleUpdatePassword();
  };

  useEffect(() => {
    if (!accountData || !accountData.userAuthen) return;
    const { userAuthen } = accountData;

    userAuthen.userActualManagingOrgType = convertOrgType(userAuthen.userActualManagingOrgType);

    setUserInfo({
      ...accountData,
      email: `${accountData.userAuthen.email || accountData.userAuthen.unverifiedEmail}`,
      // expiredAt === null: không có ngày hết hạn
      noExpired: userAuthen && Boolean(!userAuthen.expiredAt),
      // deletedAt === null: chưa bị xóa -> Trạng thái quản trị = true
      status: userAuthen && !userAuthen.inactive,
    });
  }, [accountData]);

  const contextValue: UserInfoDataContextType = {
    // Data
    userInfo,
    password,

    // Actions
    setUserData,
    setPassword,
    setDefaultPassword,
    handleUpdateUserInfo,
    handleUpdatePassword,

    // Computed properties
    isClient,
  };

  return (
    <UserInfoDataContext.Provider value={contextValue}>{children}</UserInfoDataContext.Provider>
  );
};
